/**
 * Impersonation Store
 *
 * Manages "view as another user" state for platform admins.
 * Backs up original tokens before swapping in impersonated user tokens,
 * and restores them on stop.
 */
import { create } from 'zustand';
import {
  getCachedLocalStorage,
  setCachedLocalStorage,
  removeCachedLocalStorage,
} from '../utils/storage';
import * as orgApi from '../services/api/org';

const BACKUP_KEYS = [
  'quazi_access_token',
  'quazi_id_token',
  'quazi_refresh_token',
  'quazi_user',
] as const;

const backupPrefix = (k: string) => `quazi_imp_bak_${k}`;

interface ImpersonationState {
  active: boolean;
  targetDisplayName: string | null;
  targetEmail: string | null;
  targetOrgName: string | null;
  loading: boolean;
}

interface ImpersonationActions {
  startImpersonation: (userId: string, displayName: string | null, email: string, orgName: string | null) => Promise<void>;
  stopImpersonation: () => void;
  hydrate: () => void;
}

export const useImpersonationStore = create<ImpersonationState & ImpersonationActions>(
  (set, get) => ({
    active: false,
    targetDisplayName: null,
    targetEmail: null,
    targetOrgName: null,
    loading: false,

    hydrate: () => {
      const backed = getCachedLocalStorage(backupPrefix('quazi_access_token'));
      if (backed) {
        set({
          active: true,
          targetDisplayName: getCachedLocalStorage('quazi_imp_display_name'),
          targetEmail: getCachedLocalStorage('quazi_imp_email'),
          targetOrgName: getCachedLocalStorage('quazi_imp_org_name'),
        });
      }
    },

    startImpersonation: async (userId, displayName, email, orgName) => {
      set({ loading: true });
      try {
        const tokens = await orgApi.impersonateUser(userId);

        for (const key of BACKUP_KEYS) {
          const val = getCachedLocalStorage(key);
          if (val) setCachedLocalStorage(backupPrefix(key), val);
        }

        setCachedLocalStorage('quazi_access_token', tokens.access_token);
        if (tokens.id_token) setCachedLocalStorage('quazi_id_token', tokens.id_token);
        if (tokens.refresh_token) setCachedLocalStorage('quazi_refresh_token', tokens.refresh_token);
        setCachedLocalStorage('quazi_user', email);

        setCachedLocalStorage('quazi_imp_display_name', displayName || '');
        setCachedLocalStorage('quazi_imp_email', email);
        setCachedLocalStorage('quazi_imp_org_name', orgName || '');

        set({
          active: true,
          targetDisplayName: displayName,
          targetEmail: email,
          targetOrgName: orgName,
          loading: false,
        });

        window.location.reload();
      } catch (err) {
        set({ loading: false });
        throw err;
      }
    },

    stopImpersonation: () => {
      for (const key of BACKUP_KEYS) {
        const val = getCachedLocalStorage(backupPrefix(key));
        if (val) {
          setCachedLocalStorage(key, val);
          removeCachedLocalStorage(backupPrefix(key));
        }
      }

      removeCachedLocalStorage('quazi_imp_display_name');
      removeCachedLocalStorage('quazi_imp_email');
      removeCachedLocalStorage('quazi_imp_org_name');

      set({
        active: false,
        targetDisplayName: null,
        targetEmail: null,
        targetOrgName: null,
        loading: false,
      });

      window.location.reload();
    },
  }),
);
